// src/components/Drive/DeliveryList/LocationModal.jsx
import React, { useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, CircularProgress,
} from "@mui/material";
import { MapPin, LocateFixed } from "lucide-react";
import { useTranslation } from "react-i18next";

import { getCourierLocationPayload } from "../../../utils/location";
import { LocationAutocomplete } from "../../../shared/components/AutoComplete/LocationAutocomplete";

export function LocationModal({ open, onClose, currentCity, onConfirm }) {
  const { t } = useTranslation();
  const [locating, setLocating]               = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [inputValue, setInputValue]           = useState(currentCity || "");

  const handleDetect = async () => {
    setLocating(true);
    const payload = await getCourierLocationPayload();
    setLocating(false);
    if (payload) {
      setSelectedLocation(payload);
      setInputValue(t("drive.locationModal.currentLocation"));
    }
  };

  const handleLocationSelect = (locationData) => {
    setSelectedLocation({
      coordinates: { lat: locationData.latitude, long: locationData.longitude },
      city:    locationData.city    || inputValue,
      address: locationData.address,
    });
  };

  const handleConfirm = () => {
    if (selectedLocation) onConfirm(selectedLocation);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <MapPin size={18} />
          <Typography variant="h6" fontWeight={700}>
            {t("drive.locationModal.title")}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("drive.locationModal.description")}
        </Typography>
        <LocationAutocomplete
          label={t("drive.locationModal.label")}
          placeholder={t("drive.locationModal.placeholder")}
          value={inputValue}
          onChange={setInputValue}
          onLocationSelect={handleLocationSelect}
          showDetectLocation={false}
        />
        <Button
          fullWidth
          variant="outlined"
          startIcon={locating ? <CircularProgress size={15} /> : <LocateFixed size={15} />}
          onClick={handleDetect}
          disabled={locating}
          sx={{ mt: 1.5, textTransform: "none", fontWeight: 600 }}
        >
          {locating
            ? t("drive.locationModal.locating")
            : t("drive.locationModal.useCurrentLocation")}
        </Button>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          {t("common.cancel")}
        </Button>
        <Button
          variant="contained"
          disableElevation
          disabled={!selectedLocation}
          onClick={handleConfirm}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          {t("common.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
